import React, { useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { CSS } from '@dnd-kit/utilities';

// Minimal renderer: convert tiptap-like JSON content into basic HTML for previews.
function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function nodeToHTML(node) {
  if (!node) return '';
  if (node.type === 'text') {
    let txt = escapeHtml(node.text || '');
    if (Array.isArray(node.marks)) {
      node.marks.forEach((m) => {
        if (m.type === 'bold') txt = `<strong>${txt}</strong>`;
        if (m.type === 'italic') txt = `<em>${txt}</em>`;
        if (m.type === 'underline') txt = `<u>${txt}</u>`;
        if (m.type === 'link' && m.attrs && m.attrs.href)
          txt = `<a href="${escapeHtml(m.attrs.href)}" target="_blank" rel="noreferrer">${txt}</a>`;
      });
    }
    return txt;
  }

  const children = (node.content || []).map(nodeToHTML).join('');

  switch (node.type) {
    case 'paragraph':
      return `<p>${children}</p>`;

    case 'heading': {
      const lvl = (node.attrs && node.attrs.level) || 2;
      return `<h${lvl} style="font-size:${Math.max(14, 22 - lvl * 2)}px;margin:6px 0">${children}</h${lvl}>`;
    }

    case 'image':
      return `<img src="${escapeHtml(node.attrs?.src || '')}" style="max-width:100%;height:auto;display:block"/>`;

    case 'hard_break':
      return '<br/>';

    default:
      return children;
  }
}

function contentToHTML(content) {
  if (!content) return '';
  if (typeof content === 'string') return content;
  // tiptap doc shape
  const nodes =
    content.type === 'doc' && Array.isArray(content.content)
      ? content.content
      : Array.isArray(content)
        ? content
        : content.content || [];
  return nodes.map(nodeToHTML).join('');
}
function SortablePage({ p, i, activePage, setActivePage, deletePage, isEdit }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: p.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col items-start w-full transition-all duration-200 ${
        isDragging ? 'scale-105 opacity-80 shadow-2xl rotate-[1deg]' : ''
      }`}
    >
      {/* Drag Handle */}
      {isEdit && (
        <div
          {...attributes}
          {...listeners}
          className="
    absolute top-2 left-2 z-50
    w-8 h-8 flex items-center justify-center

    bg-white/80 backdrop-blur-md
    border border-gray-200 rounded-lg
    shadow-sm

    cursor-grab active:cursor-grabbing

    opacity-60 hover:opacity-100
    transition-all duration-200

    hover:scale-110
  "
        >
          <GripVertical size={16} className="text-gray-600" />
        </div>
      )}

      {/* Page Card */}
      <button onClick={() => setActivePage(p.id)} className="w-full">
        <div
          className={`
    relative overflow-hidden rounded-xl border bg-white
    transition-all duration-200 ease-out w-[160px] hover:scale-[1.02] hover:shadow-lg hover:-translate-y-[2px]
    ${
      activePage === p.id
        ? 'border-[#375AE7] ring-2 ring-[#375AE7]/20 shadow-lg'
        : 'border-gray-200 hover:border-[#375AE7]/40 hover:shadow-md'
    }
  `}
          style={{ height: '200px' }}
        >
          <div
            className="
  absolute left-0 top-0 bottom-0 w-[3px]
  bg-transparent group-hover:bg-[#375AE7] group-hover:cursor-pointer
  transition-all duration-200
  rounded-l-xl
"
          />
          {/* Preview */}
          <div
            className="absolute inset-0 bg-white"
            style={{
              transform: 'scale(0.2)',
              transformOrigin: 'top left',
              width: '794px',
              height: '1123px',
              pointerEvents: 'none',
            }}
            dangerouslySetInnerHTML={{
              __html:
                p?.html ??
                (typeof p?.content === 'string'
                  ? p.content
                  : contentToHTML(p?.content)) ??
                "<div style='color:#bbb;font-size:14px;padding:10px'>Empty page</div>",
            }}
          />

          {/* Overlay Glow */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent pointer-events-none" />
        </div>
      </button>

      {/* Footer */}
      <div className="mt-2 flex items-center justify-between w-[160px]">
        <span className="text-xs font-medium text-gray-600">Page {i + 1}</span>

        {isEdit && (
          <button
            onClick={() => deletePage(p.id)}
            className="text-gray-400 hover:text-red-500 transition"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
export default function PagesPanel({
  pages,
  activePage,
  setActivePage,
  isEdit,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // prevents accidental drag
      },
    }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = pages.findIndex((p) => p.id === active.id);
    const newIndex = pages.findIndex((p) => p.id === over.id);

    const updated = arrayMove(pages, oldIndex, newIndex);

    window.dispatchEvent(
      new CustomEvent('rp:updatePages', {
        detail: { pages: updated },
      }),
    );
  };

  // Ensure the editor shows the first page by default when pages change.
  useEffect(() => {
    if (typeof setActivePage !== 'function') return;
    if (!Array.isArray(pages) || pages.length === 0) return;

    const exists = pages.some((p) => p.id === activePage);
    if (!activePage || !exists) {
      setActivePage(pages[0].id);
    }
  }, [pages, activePage, setActivePage]);

  return (
    <div
      className="
  w-[220px] min-w-[220px] max-w-[220px]
  h-full
  bg-gradient-to-b from-white to-[#F8FAFC]
  border-r border-gray-200
  p-4
  overflow-y-auto
"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-sm font-semibold text-gray-800">Pages</h2>

        {isEdit && (
          <button
            onClick={() => {
              const next = [
                ...(pages || []),
                { id: `page-${Date.now()}`, html: '', content: null },
              ];
              window.dispatchEvent(
                new CustomEvent('rp:updatePages', {
                  detail: { pages: next },
                }),
              );
            }}

            className="
      flex items-center gap-1
      px-3 py-1.5 text-xs font-medium
      bg-[#375AE7] text-white
      rounded-lg shadow-sm
      hover:bg-[#2F4FD1]
      active:scale-95 transition
    "
          >
            + Add
          </button>
        )}
      </div>

      {/* Drag List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={(pages || []).map((p) => p.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-5">
            {(pages || []).map((p, i) => (
              <SortablePage
                key={p.id}
                p={p}
                i={i}
                isEdit={isEdit}
                activePage={activePage}
                setActivePage={setActivePage}
                deletePage={(id) => {
                  const next = pages.filter((x) => x.id !== id);
                  window.dispatchEvent(
                    new CustomEvent('rp:updatePages', {
                      detail: { pages: next },
                    }),
                  );
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
